import { createSelector } from '@reduxjs/toolkit'
import _, { first, sortBy } from 'lodash'
import { RootState } from 'shared/config/store'
import { getDateRange } from 'shared/utils/access'
import { SORT_KEY } from 'features/common/model/slice/appSlice'

import { NodeTypes } from '../../../../shared/constants/common'
import { assetsSelectors } from '../slice/assetsSlice'
import { selectSelectedSort, selectSortOrder } from '../../../common/model/selectors/appSelectors'
import { sortComparer } from '../../../common/utils/itemsSort'

export const selectedAssetsIdsSelector = (state: RootState) => state.assets.selectedItemsIds

export const allAssetsEntitiesSelector = (state: RootState) => {
  const assets = assetsSelectors.selectAll(state.assets)
  return assets
}

export const allAssetsSelector = (state: RootState) =>
  assetsSelectors
    .selectAll(state.assets)

export const areAllAssetsFetchedSelector = (state: RootState) => state.assets.areAllAssetsFetched

export const assetsStatusSelector = (state: RootState) => state.assets.status

export const systemFieldsSelector = (state: RootState) => state.assets.systemFields

export const getFirstPageStatusSelector = (state: RootState) => state.assets.firstPageStatus

export const getSelectedAssetsFilterSelector = (state: RootState) => state.assets.selectedFilter

export const getLastAssetCreatedSelector = createSelector(
  allAssetsEntitiesSelector,
  (assets) => {
    if (assets.length === 0) return null
    const sortedByCreated = sortBy(assets, (asset) => new Date(asset.created).getTime())
    const asset = first(sortedByCreated)
    return asset.created
  }
)

export const getNextPageStatusSelector = (state: RootState) => state.assets.nextPageStatus

export const getAllAssetFilteredByDateSelector = createSelector(
  allAssetsEntitiesSelector,
  getSelectedAssetsFilterSelector,
  (assets, selectedFilter) => {
    const dateRange = getDateRange(selectedFilter)
    const [createdGte, createdLte] = dateRange

    // Если фильтр не установлен, возвращаем все ассеты
    if (_.isNil(createdGte) || _.isNil(createdLte)) {
      return assets
    }

    // Преобразуем даты в timestamp один раз для оптимизации
    const gteTimestamp = new Date(createdGte).getTime()
    const lteTimestamp = new Date(createdLte).getTime()

    return assets.filter((asset) => {
      // Валидация даты создания ассета
      if (!asset.created) return false

      const assetCreatedTimestamp = new Date(asset.created).getTime()

      // Проверяем, что дата корректна (не NaN)
      if (isNaN(assetCreatedTimestamp)) return false

      return assetCreatedTimestamp >= gteTimestamp && assetCreatedTimestamp <= lteTimestamp
    })
  }
)

const filterAssetsByType = (type: NodeTypes) => (state: RootState) => {
  const allAssets = assetsSelectors.selectAll(state.assets)
  return allAssets.filter((asset) => asset.type === type)
}

export const getAllFileAssetsSelector = filterAssetsByType(NodeTypes.FILE)
export const getAllLinkAssetsSelector = filterAssetsByType(NodeTypes.LINK)

const createSortedAssetsSelector = (assetsSelector: (state: RootState) => any[]) =>
  createSelector(
    [assetsSelector, selectSelectedSort, selectSortOrder],
    (assets, sortKey, sortDirection) => {
      if (!assets.length) return assets
      return [...assets].sort(sortComparer.bind(null, { sortDirection, sortKey }))
    }
  )

export const selectSortedFileAssets = createSortedAssetsSelector(getAllFileAssetsSelector)
export const selectSortedLinkAssets = createSortedAssetsSelector(getAllLinkAssetsSelector)

